import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ComparisonProvider } from "./contexts/ComparisonContext";
import { AuthProvider } from "./contexts/AuthContext";
import { SearchProvider } from "./contexts/SearchContext";
import { motion, AnimatePresence } from "framer-motion";
import CookieConsent from "./components/CookieConsent";

// Lazy-loaded pages
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import AIRecommendations from "./pages/AIRecommendations";
import SubmitBot from "./pages/SubmitBot";
import Credits from "./pages/Credits";
import CreditHistory from "./pages/CreditHistory";
import ComparePage from "./pages/ComparePage";
import Admin from "./pages/Admin";
import AdminSetup from "./pages/AdminSetup";
import DiscoverySettings from "./pages/DiscoverySettings";
import BotDetail from "./pages/BotDetail";
import NotFound from "./pages/NotFound";
import OAuthConsent from "./pages/OAuthConsent";
import Deals from "./pages/Deals";
import Category from "./pages/Category";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import SearchResults from "./pages/SearchResults";
import Quiz from "./pages/Quiz";

// SEO pages
import BestAIToolsForStudents from "./pages/seo/BestAIToolsForStudents";
import BestAIToolsForCoding from "./pages/seo/BestAIToolsForCoding";
import FreeAITools2026 from "./pages/seo/FreeAITools2026";
import ChatGPTvsClaude2026 from "./pages/seo/ChatGPTvsClaude2026";
import BestFreeAIChatbots2026 from "./pages/seo/BestFreeAIChatbots2026";
import BestAIToolsForResearch from "./pages/seo/BestAIToolsForResearch";
import HowWeReviewTools from "./pages/seo/HowWeReviewTools";
import About from "./pages/seo/About";
import Contact from "./pages/seo/Contact";
import Terms from "./pages/seo/Terms";

const queryClient = new QueryClient();

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 }
};

const PageWrap: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.2 }}>
    {children}
  </motion.div>
);

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageWrap><Index /></PageWrap>} />
        <Route path="/auth" element={<PageWrap><Auth /></PageWrap>} />
        <Route path="/dashboard" element={<PageWrap><Dashboard /></PageWrap>} />
        <Route path="/profile" element={<PageWrap><Profile /></PageWrap>} />
        <Route path="/settings" element={<PageWrap><Settings /></PageWrap>} />
        <Route path="/ai-recommendations" element={<PageWrap><AIRecommendations /></PageWrap>} />
        <Route path="/submit-bot" element={<PageWrap><SubmitBot /></PageWrap>} />
        <Route path="/credits" element={<PageWrap><Credits /></PageWrap>} />
        <Route path="/credit-history" element={<PageWrap><CreditHistory /></PageWrap>} />
        <Route path="/compare" element={<PageWrap><ComparePage /></PageWrap>} />
        <Route path="/admin" element={<PageWrap><Admin /></PageWrap>} />
        <Route path="/admin-setup" element={<PageWrap><AdminSetup /></PageWrap>} />
        <Route path="/discovery-settings" element={<PageWrap><DiscoverySettings /></PageWrap>} />
        <Route path="/bot/:id" element={<PageWrap><BotDetail /></PageWrap>} />
        <Route path="/oauth/consent" element={<PageWrap><OAuthConsent /></PageWrap>} />
        <Route path="/deals" element={<PageWrap><Deals /></PageWrap>} />
        <Route path="/category/:categoryId" element={<PageWrap><Category /></PageWrap>} />
        <Route path="/search" element={<PageWrap><SearchResults /></PageWrap>} />
        <Route path="/privacy-policy" element={<PageWrap><PrivacyPolicy /></PageWrap>} />
        <Route path="/quiz" element={<PageWrap><Quiz /></PageWrap>} />
        {/* SEO Landing Pages */}
        <Route path="/best-ai-tools-for-students" element={<PageWrap><BestAIToolsForStudents /></PageWrap>} />
        <Route path="/best-ai-tools-for-coding" element={<PageWrap><BestAIToolsForCoding /></PageWrap>} />
        <Route path="/free-ai-tools-2026" element={<PageWrap><FreeAITools2026 /></PageWrap>} />
        <Route path="/chatgpt-vs-claude" element={<PageWrap><ChatGPTvsClaude2026 /></PageWrap>} />
        <Route path="/best-free-ai-chatbots" element={<PageWrap><BestFreeAIChatbots2026 /></PageWrap>} />
        <Route path="/best-ai-tools-for-research" element={<PageWrap><BestAIToolsForResearch /></PageWrap>} />
        <Route path="/how-we-review" element={<PageWrap><HowWeReviewTools /></PageWrap>} />
        <Route path="/about" element={<PageWrap><About /></PageWrap>} />
        <Route path="/contact" element={<PageWrap><Contact /></PageWrap>} />
        <Route path="/terms" element={<PageWrap><Terms /></PageWrap>} />
        <Route path="*" element={<PageWrap><NotFound /></PageWrap>} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false} storageKey="vergo-theme">
        <TooltipProvider>
          <AuthProvider>
            <SearchProvider>
              <ComparisonProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <AnimatedRoutes />
                  <CookieConsent />
                </BrowserRouter>
              </ComparisonProvider>
            </SearchProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
